import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, PawPrint } from "lucide-react";
import SEO from "../components/SEO";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import CampaignThermometer from "../components/CampaignThermometer";
import DonorWall from "../components/DonorWall";
import RecentTributes from "../components/RecentTributes";
import TopFundraisers from "../components/TopFundraisers";
import ClassLeaderboard from "../components/ClassLeaderboard";
import FeaturedPoll from "../components/FeaturedPoll";
import { Editable, EditableImage } from "../components/Editable";
import { useContentApi } from "../context/ContentContext";
import { mailbox } from "../lib/mailboxes";
import { SITE_URL } from "../lib/publicSite";

const Home = () => {
    const [featured, setFeatured] = useState([]);
    const [stats, setStats] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const { c } = useContentApi();

    useEffect(() => {
        api.get("/products", { params: { featured: true } })
            .then((r) => setFeatured(r.data.slice(0, 5)))
            .catch(() => {});
        api.get("/stats/public")
            .then((r) => setStats(r.data))
            .catch(() => {});
        api.get("/campaigns/public")
            .then((r) => setCampaigns(r.data.filter((cam) => cam.featured).slice(0, 2)))
            .catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            <SEO
                title="Support Frenchtown, Montana"
                description="Support school, family and community needs in Frenchtown, Montana. Donate, shop and follow Reddog Community Foundation's public ledger."
                path="/"
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        name: "Reddog Community Foundation",
                        url: SITE_URL,
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        name: "Reddog Community Foundation — Home",
                        breadcrumb: {
                            "@type": "BreadcrumbList",
                            itemListElement: [
                                { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
                            ],
                        },
                    },
                ]}
            />
            {/* Hero */}
            <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-12 lg:pt-20 pb-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-6 fade-up">
                        <Editable
                            contentKey="hero.tag"
                            as="p"
                            className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-6"
                        >
                            Reddog Community Foundation · Transparent fundraising for Frenchtown
                        </Editable>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1C252E] font-medium leading-[1.05] tracking-tight">
                            <Editable contentKey="hero.title1" as="span">Helping Frenchtown</Editable>
                            <br />
                            <Editable contentKey="hero.title2" as="span" className="text-[#E53935]">
                                fund real needs
                            </Editable>
                            <br />
                            <Editable contentKey="hero.title3" as="span">with transparency.</Editable>
                        </h1>
                        <Editable
                            contentKey="hero.sub"
                            as="p"
                            multiline
                            className="mt-8 text-lg text-[#4A5568] leading-relaxed max-w-xl"
                        >
                            Reddog Community Foundation Inc. supports schools, families, and community needs through restricted fundraising, responsible oversight, volunteer expertise, and public accountability.
                        </Editable>
                        <div className="mt-10 flex flex-col sm:flex-row gap-3 flex-wrap">
                            <Link
                                to="/donate"
                                data-testid="hero-donate-btn"
                                className="inline-flex items-center justify-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-7 py-4 font-medium transition-all hover:-translate-y-0.5"
                            >
                                <Editable contentKey="hero.cta_donate" as="span">Donate to a Fund</Editable>
                                <ArrowRight className="w-4 h-4" strokeWidth={2} />
                            </Link>
                            <Link
                                to="/ledger"
                                data-testid="hero-ledger-btn"
                                className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#1C252E] text-[#1C252E] hover:bg-[#1C252E] hover:text-white rounded-full px-7 py-4 font-medium transition-all"
                            >
                                <Editable contentKey="hero.cta_ledger" as="span">View Public Ledger</Editable>
                            </Link>
                            <a
                                href={`mailto:${mailbox(c, "sales")}?subject=Suggest a Need`}
                                data-testid="hero-suggest-btn"
                                className="inline-flex items-center justify-center gap-2 text-[#1C252E] hover:text-[#E53935] underline-offset-4 hover:underline rounded-full px-7 py-4 font-medium transition-all"
                            >
                                <Editable contentKey="hero.cta_suggest" as="span">Suggest a Need</Editable>
                            </a>
                        </div>
                        <div className="mt-12 grid grid-cols-3 gap-6 pt-8 border-t border-black/10">
                            <div data-testid="hero-stat-families">
                                <p className="font-heading text-3xl text-[#1C252E] font-medium">
                                    {stats ? stats.donations_count.toLocaleString() : "—"}
                                </p>
                                <Editable
                                    contentKey="hero.stat_students_label"
                                    as="p"
                                    className="text-xs text-[#4A5568] uppercase tracking-wider mt-1"
                                >
                                    Donations tracked
                                </Editable>
                            </div>
                            <div data-testid="hero-stat-pets">
                                <p className="font-heading text-3xl text-[#1C252E] font-medium">
                                    {stats ? `$${Math.round(stats.impact_total).toLocaleString()}` : "—"}
                                </p>
                                <Editable
                                    contentKey="hero.stat_raised_label"
                                    as="p"
                                    className="text-xs text-[#4A5568] uppercase tracking-wider mt-1"
                                >
                                    Raised to date
                                </Editable>
                            </div>
                            <div data-testid="hero-stat-volunteers">
                                <p className="font-heading text-3xl text-[#1C252E] font-medium">
                                    {stats ? stats.active_funds_count.toLocaleString() : "—"}
                                </p>
                                <Editable
                                    contentKey="hero.stat_programs_label"
                                    as="p"
                                    className="text-xs text-[#4A5568] uppercase tracking-wider mt-1"
                                >
                                    Funds open
                                </Editable>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-6 fade-up-delay-1">
                        <div className="relative">
                            <EditableImage
                                contentKey="hero.image"
                                alt="School community"
                                className="aspect-[4/5] w-full rounded-3xl bg-[#F4F1EA] shadow-[0_20px_60px_rgba(0,0,0,0.08)] object-cover"
                                fallbackSrc="https://images.unsplash.com/photo-1601758064955-a4a16da74a86?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                            />
                            <div className="absolute -bottom-8 -left-6 sm:-left-12 bg-white rounded-2xl shadow-xl p-6 max-w-xs border border-black/5" data-testid="home-impact-card">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-[#F2D8D5] flex items-center justify-center">
                                        <Heart className="w-5 h-5 text-[#E53935]" fill="#E53935" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <Editable contentKey="hero.impact_title" as="p" className="font-heading text-lg text-[#1C252E] font-medium">
                                            Restricted fund tracking
                                        </Editable>
                                        <Editable contentKey="hero.impact_sub" as="p" className="text-xs text-[#4A5568]">
                                            Donations are disbursed only for the approved purpose
                                        </Editable>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Impact Tracker */}
            {stats && (
                <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-8" data-testid="home-impact-tracker">
                    <div className="bg-gradient-to-br from-[#F2D8D5] via-[#FDFBF7] to-[#F4F1EA] border border-black/5 rounded-3xl p-8 lg:p-12">
                        <div className="text-center mb-8">
                            <Editable contentKey="impact.tag" as="p" className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">
                                Live community support tracker
                            </Editable>
                            <h2 className="font-heading text-2xl sm:text-3xl text-[#1C252E] font-medium tracking-tight">
                                <Editable contentKey="impact.headline_prefix" as="span">Total support generated so far:</Editable>{" "}
                                <span className="text-[#E53935]">${Math.round(stats.impact_total).toLocaleString()}</span>
                            </h2>
                            <p className="mt-3 text-xs text-[#4A5568] max-w-2xl mx-auto">
                                Includes <strong>donations</strong> + <strong>net proceeds from merchandise sales</strong>. For donation-only inflows and approved payouts, see the{" "}
                                <Link to="/ledger" className="text-[#E53935] underline-offset-2 hover:underline font-medium">Public Ledger</Link>.
                            </p>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                            {[
                                { label: "Donations", value: `$${Math.round(stats.donations_total).toLocaleString()}`, sub: `${stats.donations_count} gifts received` },
                                { label: "Merch Sales", value: `$${Math.round(stats.orders_revenue).toLocaleString()}`, sub: `${stats.orders_count} orders · net to General Support Fund` },
                                { label: "Funds Open", value: stats.active_funds_count.toLocaleString(), sub: "accepting support right now" },
                                { label: "Total Support", value: `$${Math.round(stats.impact_total).toLocaleString()}`, sub: "donations + merch combined" },
                            ].map((m) => (
                                <div key={m.label} className="text-center" data-testid={`impact-${m.label.toLowerCase().replace(/\s/g, "-")}`}>
                                    <p className="font-heading text-3xl lg:text-4xl text-[#1C252E] font-medium">{m.value}</p>
                                    <p className="text-xs uppercase tracking-wider text-[#E53935] font-bold mt-2">{m.label}</p>
                                    <p className="text-xs text-[#4A5568] mt-1">{m.sub}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Campaign Thermometers */}
            {campaigns.length > 0 && (
                <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-8 mt-12" data-testid="home-campaigns-section">
                    <div className="mb-8 max-w-2xl">
                        <Editable contentKey="campaigns.tag" as="p" className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">
                            Active fundraisers
                        </Editable>
                        <Editable contentKey="campaigns.headline" as="h2" className="font-heading text-3xl sm:text-4xl text-[#1C252E] font-medium tracking-tight">
                            Help us hit our goals.
                        </Editable>
                    </div>
                    <div className={`grid gap-6 ${campaigns.length > 1 ? "lg:grid-cols-2" : "grid-cols-1"}`} data-testid="home-campaigns-grid">
                        {campaigns.map((cam) => (
                            <CampaignThermometer key={cam.id} campaign={cam} compact={campaigns.length > 1} />
                        ))}
                    </div>
                </section>
            )}

            {/* Featured Products */}
            <section className="bg-[#F4F1EA] py-20 lg:py-32 mt-20">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="flex items-end justify-between mb-12">
                        <div>
                            <Editable contentKey="featured.tag" as="p" className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">
                                Spirit shop
                            </Editable>
                            <Editable contentKey="featured.headline" as="h2" className="font-heading text-3xl sm:text-4xl text-[#1C252E] font-medium tracking-tight">
                                Wear Reddog. Support the mission.
                            </Editable>
                        </div>
                        <Link
                            to="/shop"
                            data-testid="featured-view-all-link"
                            className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#1C252E] hover:text-[#E53935] transition-colors"
                        >
                            View all
                            <ArrowRight className="w-4 h-4" strokeWidth={2} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8" data-testid="featured-products-grid">
                        {featured.map((p, i) => (
                            <ProductCard key={p.id} product={p} featured={i === 0} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Mission */}
            <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    <EditableImage
                        contentKey="mission.image"
                        alt="Our mission"
                        className="aspect-[5/4] w-full rounded-3xl bg-[#F4F1EA] object-cover"
                        fallbackSrc="https://images.unsplash.com/photo-1597595735637-05a49627ee29?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                    />
                    <div>
                        <Editable contentKey="mission.tag" as="p" className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">
                            Our mission
                        </Editable>
                        <h2 className="font-heading text-3xl sm:text-4xl text-[#1C252E] font-medium leading-tight tracking-tight mb-6">
                            <Editable contentKey="mission.headline1" as="span">Stronger classrooms,</Editable>
                            <br />
                            <Editable contentKey="mission.headline2" as="span">brighter futures.</Editable>
                        </h2>
                        <Editable
                            contentKey="mission.body"
                            as="p"
                            multiline
                            className="text-lg text-[#4A5568] leading-relaxed mb-8"
                        >
                            Reddog Community Foundation is the parent-led nonprofit that bridges home and school.
                        </Editable>
                        <div className="space-y-5">
                            {[
                                { icon: Users, titleKey: "mission.card1_title", bodyKey: "mission.card1_body" },
                                { icon: PawPrint, titleKey: "mission.card2_title", bodyKey: "mission.card2_body" },
                                { icon: Heart, titleKey: "mission.card3_title", bodyKey: "mission.card3_body" },
                            ].map((it) => (
                                <div key={it.titleKey} className="flex gap-4">
                                    <div className="w-10 h-10 rounded-full bg-[#F2D8D5] flex items-center justify-center flex-shrink-0">
                                        <it.icon className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} />
                                    </div>
                                    <div className="flex-1">
                                        <Editable contentKey={it.titleKey} as="p" className="font-heading text-lg text-[#1C252E] font-medium">
                                            {c(it.titleKey, "Title")}
                                        </Editable>
                                        <Editable contentKey={it.bodyKey} as="p" multiline className="text-sm text-[#4A5568]">
                                            {c(it.bodyKey, "Body")}
                                        </Editable>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Donor Wall */}
            <DonorWall />

            {/* Community voice — featured open poll (renders nothing if none active) */}
            <FeaturedPoll />

            {/* Recent Tributes — non-anonymous "in honor of" gifts as social proof */}
            <RecentTributes />

            {/* Top P2P fundraisers — neighbors raising for neighbors */}
            <TopFundraisers />

            {/* School Repair & Community Support Fund — featured program */}
            <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-20">
                <div className="bg-gradient-to-br from-[#F2D8D5] to-[#FDFBF7] border border-[#E53935]/15 rounded-3xl p-8 sm:p-12 lg:p-16" data-testid="program-repair-section">
                    <Editable contentKey="program.repair.tag" as="p" className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3 flex items-center gap-2">
                        Featured program
                    </Editable>
                    <Editable
                        contentKey="program.repair.title"
                        as="h2"
                        className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#1C252E] font-medium tracking-tight leading-tight mb-6 max-w-3xl"
                    >
                        School Repair & Community Support Fund
                    </Editable>
                    <Editable
                        contentKey="program.repair.body"
                        as="p"
                        multiline
                        className="text-[#4A5568] leading-relaxed text-lg max-w-3xl mb-8"
                    >
                        The School Repair & Community Support Fund helps raise and manage restricted donations for approved school and community needs, including repairs, safety improvements, educational resources, playgrounds, libraries, and emergency facility support. Funds are tracked publicly and disbursed only for the approved purpose.
                    </Editable>
                    <Link
                        to="/ledger"
                        data-testid="program-repair-cta"
                        className="inline-flex items-center gap-2 bg-[#1C252E] text-white hover:bg-black rounded-full px-8 py-4 text-base font-medium transition-all hover:-translate-y-0.5"
                    >
                        <Editable contentKey="program.repair.cta" as="span">View the public ledger</Editable>
                        <ArrowRight className="w-5 h-5" strokeWidth={2} />
                    </Link>
                </div>
            </section>

            {/* Class Competition Leaderboard */}
            <ClassLeaderboard />

            {/* Donate CTA */}
            <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-20 lg:pb-32">
                <div className="bg-[#1C252E] rounded-3xl px-8 py-16 lg:p-20 relative overflow-hidden" data-testid="donate-cta-banner">
                    <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#E53935]/20 rounded-full blur-3xl"></div>
                    <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div>
                            <Editable contentKey="cta.tag" as="p" className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-4">
                                Make an impact
                            </Editable>
                            <Editable
                                contentKey="cta.headline"
                                as="h2"
                                multiline
                                className="font-heading text-3xl sm:text-4xl text-white font-medium leading-tight mb-6"
                            >
                                Pick a fund. Watch your dollar work.
                            </Editable>
                            <Editable
                                contentKey="cta.body"
                                as="p"
                                multiline
                                className="text-white/70 leading-relaxed text-lg max-w-lg"
                            >
                                Every Reddog purchase helps fund approved school programs, classroom needs, student support, simple repairs, field trips, and community projects.
                            </Editable>
                        </div>
                        <div className="lg:text-right">
                            <Link
                                to="/donate"
                                data-testid="donate-cta-button"
                                className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-10 py-5 text-lg font-medium transition-all hover:-translate-y-0.5"
                            >
                                <Editable contentKey="cta.button" as="span">Donate now</Editable>
                                <ArrowRight className="w-5 h-5" strokeWidth={2} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
